import React from "react";

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  full?: boolean;
  disabled?: boolean;
  loading?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  full = false,
  disabled = false,
  loading = false,
  type = 'button',
  onClick,
  children,
  className = '',
}) => {
  const baseClasses = "font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 inline-flex items-center justify-center";

  const variants = {
    primary: "bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500 disabled:bg-primary-400",
    secondary: "bg-gray-200 dark:bg-zinc-700 text-gray-800 dark:text-zinc-100 hover:bg-gray-300 dark:hover:bg-zinc-600 focus:ring-gray-500 disabled:bg-gray-100 dark:disabled:bg-zinc-800",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-400",
    success: "bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-green-400",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  const widthClass = full ? "w-full" : "";
  const disabledClass = (disabled || loading) ? "opacity-50 cursor-not-allowed" : "cursor-pointer";

  return (
    <button
      type={type}
      className={`${baseClasses} ${variants[variant]} ${sizes[size]} ${widthClass} ${disabledClass} ${className}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-2 h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {loading ? "Chargement..." : children}
    </button>
  );
};

// Composant pour la compatibilité avec l'ancien API
interface IButtonProps {
  bg?: string;
  loading?: boolean;
  canClick?: boolean;
  actionText: string;
  onClick?: any;
  full?: boolean;
}

export const LegacyButton: React.FC<IButtonProps> = ({
  loading = false,
  canClick = true,
  actionText,
  onClick = () => null,
  full = false,
}) => (
  <Button
    variant="primary"
    onClick={onClick}
    disabled={!canClick}
    loading={loading}
    full={full}
  >
    {actionText}
  </Button>
);
